// Import before component to maintain LEGOS compatibility
import './index.scss';
import './polyfill';

import { themeConfig } from '@kmx/mui-resources';
import { AppInsightsContext, AppInsightsErrorBoundary, ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import classNames from 'classnames';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import * as styles from './App.module.scss';
import InstantCashOffer from './components/InstantCashOffer';
import ClientInfoProvider, { IClientInfoContext } from './context/clientInfo';
import CustomerInfoProvider from './context/customerInfo';
import FeaturesProvider from '@kmx/io-core/context/FeaturesContext';
import FormProvider from './context/formContext';
import OfferContextProvider from './context/offerContext';
import StoreInfoProvider from './context/storeInfo';
import VehicleInfoProvider from './context/vehicleInfo';
import { IVehicleInfo } from './types/IVehicleInfo';
import { IVinEligibility } from './types/IVinEligibility';
import { IVinMatchingStylesEligibility } from './types/IVinMatchingStyles';
import { getOriginPage } from './utils/analytics';
import { v4 as uuidv4 } from 'uuid';
import { HttpClientContextProvider } from '@kmx/io-core/context/HttpClientContext';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

const EXT_DOMAINS_TO_IGNORE = ['fullstory.com', 'qualtrics.com', 'mpulse.net', 'google-analytics.com'];

const AppError = () => {
    return (
        <Container maxWidth="md" sx={{ p: 3, my: 3 }}>
            <Stack spacing={2} alignItems="center" justifyContent="center">
                <Typography variant="h5">Hmm, something went wrong</Typography>
                <Typography variant="body1">
                    We&apos;re sorry about that. Please try clearing your browser&apos;s cache and retrying this
                    process.
                </Typography>
            </Stack>
        </Container>
    );
};

const extendedTheme = createTheme(
    deepmerge(themeConfig, {
        breakpoints: {
            values: { xs: 0, sm: 440, md: 900, lg: 1200, xl: 1450 },
        },
        colorSchemes: {
            // This removes the dark color scheme.
            dark: undefined,
        },
        components: {
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        fontSize: themeConfig.typography.body1.fontSize,
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        fontSize: themeConfig.typography.body1.fontSize,
                    },
                },
            },
        },
        // Removes the dark color scheme https://mui.com/material-ui/customization/css-theme-variables/configuration/#disabling-css-color-scheme
        cssVariables: { disableCssColorScheme: true },
    })
);
const clientId = uuidv4();

export interface IAppProps {
    authRefresh: () => Promise<string>;
    bypassRefreshOfferPrompt?: boolean;
    clientFeatures: string[];
    clientFeaturesEventId?: string;
    clientInfo?: IClientInfoContext;
    customerEmail?: string;
    enabledFeatures: string[];
    extraQuestions?: string[];
    handleIneligible: boolean;
    hidden: boolean;
    offerId?: string;
    originPage?: string;
    renderPlateVinForm?: boolean;
    saveResult: boolean;
    startingMethod?: string;
    startQuote?: IVinEligibility;
    storeId?: string;
    vehicleInfo: IVehicleInfo;
    vehicleStyles?: IVinMatchingStylesEligibility;
}

const App: React.FC<IAppProps> = ({
    authRefresh,
    bypassRefreshOfferPrompt,
    clientFeatures,
    clientFeaturesEventId,
    clientInfo,
    customerEmail,
    enabledFeatures,
    extraQuestions,
    handleIneligible,
    hidden,
    offerId,
    originPage,
    renderPlateVinForm,
    saveResult,
    startingMethod,
    startQuote,
    storeId,
    vehicleInfo,
    vehicleStyles,
}) => {
    const [containerKey, setContainerKey] = useState(1); //everytime ICO is reset, this number increments
    const [isRenderingLicenseVinForm, setIsRenderingLicenseVinForm] = useState(renderPlateVinForm);
    const useInitialProps = containerKey === 1;
    const reactPlugin = new ReactPlugin();
    const appInsights = new ApplicationInsights({
        config: {
            connectionString: `${process.env.APP_INSIGHTS_CONNECTION_STRING}`,
            extensions: [reactPlugin],
        },
    });
    appInsights.loadAppInsights();
    appInsights.addDependencyInitializer((details: any) => {
        if (EXT_DOMAINS_TO_IGNORE.some(e => details.item.target.includes(e)) && details.item.success === true) {
            return false;
        }
        return true;
    });
    appInsights.addTelemetryInitializer((env: ITelemetryItem) => {
        //custom props
        env.data = env.data || {};
        env.data['application'] = 'IO-Widget';
        // this will be used to tie multiple messages[Trace, Exceptions, Dependencies] from the same Offer Details instance together.
        env.data['clientId'] = clientId;
    });

    //changing the container div's key will throw everything away and start over, resetting all of the inital state for all elements.
    const reset = useCallback((showLicenseVinForm = true) => {
        setContainerKey(prev => prev + 1);

        // Always render the license vin form on reset
        if (showLicenseVinForm) {
            setIsRenderingLicenseVinForm(true);
        }

        window.dispatchEvent(new CustomEvent('icoReset', { detail: 'ICO Reset' }));
    }, []);

    useEffect(() => {
        appInsights.trackTrace(
            { message: 'ICO container initialized.' },
            {
                offerId,
                customerEmail,
                enabledFeatures,
                clientFeatures,
            }
        );
    }, [containerKey]);

    return (
        <div key={containerKey} id="ico-container" className={classNames(styles.ico, { [styles.icoHidden]: hidden })}>
            <AppInsightsContext.Provider value={reactPlugin}>
                <AppInsightsErrorBoundary onError={AppError} appInsights={reactPlugin}>
                    <HttpClientContextProvider config={{ hostName: process.env.API_URL, getToken: authRefresh }}>
                        <FeaturesProvider
                            clientFeatures={clientFeatures}
                            clientFeaturesEventId={clientFeaturesEventId}
                            enabledFeatures={enabledFeatures}
                        >
                            <VehicleInfoProvider
                                InitialVehicleInfo={useInitialProps ? { ...vehicleInfo, isComplete: false } : undefined}
                            >
                                <CustomerInfoProvider initialCustomerEmail={customerEmail}>
                                    <ClientInfoProvider initialClientInfo={clientInfo}>
                                        <StoreInfoProvider>
                                            <OfferContextProvider
                                                handleIneligible={handleIneligible}
                                                saveResult={saveResult}
                                                extraQuestions={extraQuestions}
                                            >
                                                <FormProvider startingMethod={startingMethod} originPage={originPage}>
                                                    <ThemeProvider theme={extendedTheme}>
                                                        <InstantCashOffer
                                                            bypassRefreshOfferPrompt={bypassRefreshOfferPrompt}
                                                            enabledFeatures={enabledFeatures}
                                                            offerId={useInitialProps ? offerId : undefined}
                                                            originPage={getOriginPage(originPage)}
                                                            renderPlateVinForm={isRenderingLicenseVinForm}
                                                            reset={reset}
                                                            startQuote={useInitialProps ? startQuote : undefined}
                                                            storeId={storeId}
                                                            vehicleStyles={useInitialProps ? vehicleStyles : undefined}
                                                        />
                                                    </ThemeProvider>
                                                </FormProvider>
                                            </OfferContextProvider>
                                        </StoreInfoProvider>
                                    </ClientInfoProvider>
                                </CustomerInfoProvider>
                            </VehicleInfoProvider>
                        </FeaturesProvider>
                    </HttpClientContextProvider>
                </AppInsightsErrorBoundary>
            </AppInsightsContext.Provider>
        </div>
    );
};

export default App;
